import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Lightbulb, Moon, Plus, SearchCode } from 'lucide-react'
import { useTheme } from 'next-themes'
import { toast } from 'sonner'
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { PriorityChip, StageDot } from '@/components/common/badges'
import { AppIcon } from '@/components/common/icon-map'
import { useUiState } from '@/hooks/use-ui-state'
import { graphql } from '@/lib/api'
import { BODY_SEARCH } from '@/lib/github-queries'
import { appConfig, keys, useIssues } from '@/lib/query'
import { INSIGHTS_VIEW, VIEWS } from '@/lib/views'
import type { WorkIssue } from '@/types/github'

// Substring beats subsequence; ties break on Updated desc.
function fuzzyScore(haystack: string, needle: string): number | null {
  const h = haystack.toLowerCase()
  const n = needle.toLowerCase()
  const at = h.indexOf(n)
  if (at !== -1) return 1000 - at
  let hi = 0
  let gaps = 0
  for (const ch of n) {
    const found = h.indexOf(ch, hi)
    if (found === -1) return null
    gaps += found - hi
    hi = found + 1
  }
  return 500 - Math.min(gaps, 400)
}

const BODY_RATE_LIMIT = 30
const BODY_RATE_WINDOW_MS = 60_000

type BodyHit = { id: string; number: number; title: string; state: string; url: string }

export function CommandPalette() {
  const { paletteOpen, setPaletteOpen, setSheetIssue, setNewIssueOpen } = useUiState()
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  const { setTheme, theme } = useTheme()
  const issues = useIssues().data
  const bodyCalls = useRef<number[]>([])
  const [bodyQuery, setBodyQuery] = useState<string | null>(null)

  const bodyMode = q.startsWith('body:')
  const bodyTerm = bodyMode ? q.slice(5).trim() : ''

  const matches = useMemo(() => {
    if (bodyMode || !q.trim() || !issues) return []
    // `@login` tokens filter by assignee/author; the rest fuzzy-matches.
    const tokens = q.trim().split(/\s+/)
    const people = tokens.filter((t) => t.startsWith('@') && t.length > 1).map((t) => t.slice(1).toLowerCase())
    const term = tokens.filter((t) => !t.startsWith('@')).join(' ')
    const pool = people.length
      ? issues.filter((issue) =>
          people.every(
            (p) =>
              issue.assignees.some((a) => a.login.toLowerCase().startsWith(p)) ||
              (issue.author ?? '').toLowerCase().startsWith(p),
          ),
        )
      : issues
    return pool
      .map((issue) => ({
        issue,
        score: term ? fuzzyScore(`#${issue.number} ${issue.title}`, term) : 0,
      }))
      .filter((m): m is { issue: WorkIssue; score: number } => m.score !== null)
      .sort(
        (a, b) => b.score - a.score || b.issue.updatedAt.localeCompare(a.issue.updatedAt),
      )
      .slice(0, 12)
  }, [issues, q, bodyMode])

  const bodyResults = useQuery({
    queryKey: keys.bodySearch(bodyQuery ?? ''),
    enabled: bodyQuery !== null && bodyQuery.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const data = await graphql<{ search: { issueCount: number; nodes: BodyHit[] } }>({
        query: BODY_SEARCH,
        variables: {
          query: `repo:${appConfig.owner}/${appConfig.repo} in:body ${bodyQuery}`,
        },
      })
      return data.search
    },
  })

  function runBodySearch() {
    const now = Date.now()
    bodyCalls.current = bodyCalls.current.filter((t) => now - t < BODY_RATE_WINDOW_MS)
    if (bodyCalls.current.length >= BODY_RATE_LIMIT) {
      const oldest = bodyCalls.current[0]!
      const wait = Math.ceil((BODY_RATE_WINDOW_MS - (now - oldest)) / 1000)
      toast.error(`Search rate limit reached, wait ${wait}s`)
      return
    }
    bodyCalls.current.push(now)
    setBodyQuery(bodyTerm)
  }

  function close() {
    setPaletteOpen(false)
    setQ('')
    setBodyQuery(null)
  }

  function openIssue(number: number) {
    close()
    setSheetIssue(number)
  }

  return (
    <CommandDialog
      open={paletteOpen}
      onOpenChange={(open) => (open ? setPaletteOpen(true) : close())}
      title="Search"
      description="Search issues, views, or commands"
    >
      <Command shouldFilter={false}>
      <CommandInput
        placeholder="Search issues, views, or commands…"
        value={q}
        onValueChange={setQ}
      />
      <CommandList>
        {bodyMode ? (
          <CommandGroup heading="Full-text search">
            <CommandItem onSelect={runBodySearch} disabled={!bodyTerm}>
              <SearchCode className="size-4" />
              Search issue bodies on GitHub for “{bodyTerm || '…'}”
            </CommandItem>
            {bodyResults.data?.nodes.map((hit) => (
              <CommandItem key={hit.id} onSelect={() => openIssue(hit.number)}>
                <span className="text-faint-foreground font-mono text-[11px]">#{hit.number}</span>
                <span className="truncate">{hit.title}</span>
              </CommandItem>
            ))}
            {bodyQuery && bodyResults.isFetching && (
              <CommandItem disabled>Searching…</CommandItem>
            )}
          </CommandGroup>
        ) : (
          <>
            <CommandEmpty>No results{q ? ` for “${q}”` : ''}.</CommandEmpty>
            <CommandGroup heading="Jump to">
              {[...VIEWS, INSIGHTS_VIEW].map((view) => (
                <CommandItem
                  key={view.key}
                  keywords={[view.title]}
                  onSelect={() => {
                    close()
                    if (view.key === 'team') {
                      const area = localStorage.getItem('n3o-work-last-area')
                      navigate(area ? `/team/${area}` : '/team')
                    } else {
                      navigate(view.path)
                    }
                  }}
                >
                  <AppIcon name={view.icon} className="size-4" />
                  <span className="flex-1">{view.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {matches.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={`Issues (${matches.length} matching “${q.trim()}”)`}>
                  {matches.map(({ issue }) => (
                    <CommandItem key={issue.id} onSelect={() => openIssue(issue.number)}>
                      <span className="text-faint-foreground font-mono text-[11px]">
                        #{issue.number}
                      </span>
                      <span className="flex-1 truncate">{issue.title}</span>
                      <PriorityChip priority={issue.priority} />
                      <StageDot stage={issue.stage} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
            <CommandSeparator />
            <CommandGroup heading="Actions">
              <CommandItem
                onSelect={() => {
                  close()
                  setNewIssueOpen(true)
                }}
              >
                <Plus className="size-4" />
                <span className="flex-1">New issue</span>
              </CommandItem>
              <CommandItem onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                <Moon className="size-4" />
                <span className="flex-1">Toggle theme</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
      </Command>
      <div className="border-border text-faint-foreground flex items-center gap-1.5 border-t px-3 py-1.5 text-[10.5px]">
        <Lightbulb className="size-3 shrink-0" />
        <span className="truncate">
          Tip: <code className="font-mono">body:</code> searches full text · <code className="font-mono">@login</code> filters by person
        </span>
        <span className="ml-auto shrink-0">↑↓ navigate · ↵ open · esc close</span>
      </div>
    </CommandDialog>
  )
}
